import React, { useEffect, useMemo, useRef, useState } from "react";

const randomFromSeed = (seed) => {
  let value = seed % 2147483647;
  return () => {
    value = (value * 16807) % 2147483646 + 1;
    return (value - 1) / 2147483646;
  };
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Shooting stars appear at the SAME frequency as before (~one streak per
 * 70–125s) but each one is born in a different part of the sky, travelling
 * along its own randomized path and direction.
 */
const useShootingStar = () => {
  const [shot, setShot] = useState(null);
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    let timer = null;
    let alive = true;
    const schedule = (delayMs) => {
      timer = setTimeout(() => {
        if (!alive) return;
        const dir = Math.random() < 0.5 ? -1 : 1;
        const dxMag = 170 + Math.random() * 110;
        const dx = dir * dxMag;
        const dy = dxMag * (0.32 + Math.random() * 0.26);
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
        setShot({
          id: Date.now(),
          left: `${12 + Math.random() * 74}%`,
          top: `${4 + Math.random() * 42}%`,
          dx: `${Math.round(dx)}px`,
          dy: `${Math.round(dy)}px`,
          angle: `${Math.round(angle)}deg`,
        });
        schedule(70000 + Math.random() * 55000);
      }, delayMs);
    };
    schedule(20000 + Math.random() * 45000);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, []);
  return shot;
};

/**
 * Multi-plane gyroscope parallax — the sky reads as a deep space, not a
 * moving wallpaper. Far planes barely move; the sparse foreground moves the
 * most (still only ~8px total). The baseline slowly follows the device's
 * resting orientation so a tilted couch position doesn't lock an offset in.
 *
 * iOS 13+: DeviceOrientationEvent needs a user-gesture permission request.
 * We ask ONCE, silently, on the first tap anywhere; if unavailable or denied
 * the sky simply stays static (tx/ty never change). Reduce Motion disables
 * the whole effect.
 */
const useGyroParallax = (layers) => {
  useEffect(() => {
    if (prefersReducedMotion() || typeof window === "undefined") return undefined;
    let raf = null;
    let base = null;
    let attached = false;
    let tx = 0, ty = 0, cx = 0, cy = 0;
    const MAX_PX = 8;
    const onOrientation = (e) => {
      if (e.beta == null || e.gamma == null) return;
      if (base === null) base = { beta: e.beta, gamma: e.gamma };
      base.beta += (e.beta - base.beta) * 0.004;
      base.gamma += (e.gamma - base.gamma) * 0.004;
      const dg = Math.max(-18, Math.min(18, e.gamma - base.gamma));
      const db = Math.max(-18, Math.min(18, e.beta - base.beta));
      tx = (dg / 18) * MAX_PX;
      ty = (db / 18) * MAX_PX;
    };
    const tick = () => {
      const nx = cx + (tx - cx) * 0.045;
      const ny = cy + (ty - cy) * 0.045;
      // Skip DOM writes when converged (or backgrounded) — keeps idle cost ~0.
      if ((Math.abs(nx - cx) > 0.002 || Math.abs(ny - cy) > 0.002) && !document.hidden) {
        cx = nx; cy = ny;
        for (const { ref, factor } of layers) {
          if (ref.current) {
            ref.current.style.transform = `translate3d(${(cx * factor).toFixed(2)}px, ${(cy * factor).toFixed(2)}px, 0)`;
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    const attach = () => {
      if (!attached) {
        attached = true;
        window.addEventListener("deviceorientation", onOrientation);
      }
    };
    const D = window.DeviceOrientationEvent;
    const onFirstGesture = () => {
      D.requestPermission()
        .then((state) => { if (state === "granted") attach(); })
        .catch(() => { /* denied/unavailable → static sky */ });
    };
    let gestureBound = false;
    if (D && typeof D.requestPermission === "function") {
      gestureBound = true;
      window.addEventListener("pointerdown", onFirstGesture, { once: true });
    } else if (D) {
      attach();
    }
    raf = requestAnimationFrame(tick);
    return () => {
      if (attached) window.removeEventListener("deviceorientation", onOrientation);
      if (gestureBound) window.removeEventListener("pointerdown", onFirstGesture);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// Art-directed atmospheric haze — three huge, ultra-faint radial structures
// (indigo / violet / deep blue) that drift over minutes. Cheap: plain divs
// with radial-gradient backgrounds, transform/opacity-only animation.
const NEBULAE = [
  { key: "a", cls: "continua-nebula continua-nebula--a", style: { width: "92vw", height: "64vw", left: "-20vw", top: "4%" } },
  { key: "b", cls: "continua-nebula continua-nebula--b", style: { width: "78vw", height: "56vw", right: "-26vw", top: "32%" } },
  { key: "c", cls: "continua-nebula continua-nebula--c", style: { width: "98vw", height: "60vw", left: "4vw", bottom: "-16%" } },
];

export const StarField = ({ density = 218 }) => {
  const deepRef = useRef(null);
  const hazeRef = useRef(null);
  const midRef = useRef(null);
  const cloudFarRef = useRef(null);
  const cloudMidRef = useRef(null);
  const nearRef = useRef(null);
  const shot = useShootingStar();
  const layersRef = useRef([
    { ref: deepRef, factor: 0.3 },
    { ref: hazeRef, factor: 0.45 },
    { ref: midRef, factor: 0.6 },
    { ref: cloudFarRef, factor: 0.75 },
    { ref: cloudMidRef, factor: 0.9 },
    { ref: nearRef, factor: 1.0 },
  ]);
  useGyroParallax(layersRef.current);

  const stars = useMemo(() => {
    const random = randomFromSeed(1947);
    return Array.from({ length: density }, (_, index) => {
      // Depth tiers: ~5% sparse soft foreground, remainder split deep/mid.
      const tier = index % 20 === 3 ? "near" : index % 2 === 0 ? "deep" : "mid";
      const patient = tier === "mid" && index % 9 === 0; // brighter anchor stars
      const wander = tier === "mid" && index % 11 === 0;
      let size, dim, bright, duration;
      if (tier === "deep") {
        // Distant dust — tiny, very low contrast, near-motionless twinkle.
        size = 0.4 + random() * 0.6;
        dim = 0.05 + random() * 0.2;
        bright = Math.min(0.5, dim + 0.08 + random() * 0.12);
        duration = 40 + random() * 80;
      } else if (tier === "near") {
        // Foreground particles — a touch larger but very faint and soft.
        size = 1.1 + random() * 0.6;
        dim = 0.08 + random() * 0.1;
        bright = Math.min(0.32, dim + 0.1 + random() * 0.08);
        duration = 26 + random() * 44;
      } else {
        size = patient ? 1.3 + random() * 0.9 : 0.6 + random() * 0.9;
        dim = patient ? 0.3 + random() * 0.3 : 0.14 + random() * 0.32;
        bright = Math.min(0.95, dim + (patient ? 0.3 : 0.2) + random() * 0.28);
        duration = patient ? 66 + random() * 74 : 14 + random() * 38;
      }
      const delay = -(random() * duration);
      const wake = (random() * 1.4).toFixed(2);
      return {
        id: `star-${index}`,
        tier,
        x: `${random() * 100}%`,
        y: `${random() * 100}%`,
        size: `${size.toFixed(2)}px`,
        dim,
        bright,
        duration: `${duration.toFixed(1)}s`,
        delay: `${delay.toFixed(1)}s`,
        wake: `${wake}s`,
        className:
          tier === "near" ? "continua-star continua-star--soft"
          : wander ? "continua-star continua-star--wander"
          : patient ? "continua-star continua-star--patient continua-star--anchor"
          : "continua-star",
      };
    });
  }, [density]);

  const renderStar = (star) => (
    <span
      key={star.id}
      className="continua-star-wake"
      style={{ left: star.x, top: star.y, "--wake-delay": star.wake }}
    >
      <span
        className={star.className}
        style={{
          width: star.size,
          height: star.size,
          "--star-dim": star.dim,
          "--star-bright": star.bright,
          "--star-duration": star.duration,
          "--star-delay": star.delay,
        }}
      />
    </span>
  );

  return (
    <div
      aria-hidden="true"
      data-testid="starfield-background"
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden continua-sky"
    >
      {/* 1 — distant stars (least movement) */}
      <div ref={deepRef} className="absolute inset-0 will-change-transform" data-testid="sky-layer-deep">
        {stars.filter((s) => s.tier === "deep").map(renderStar)}
      </div>
      {/* 2 — atmospheric haze / nebula structures */}
      <div ref={hazeRef} className="absolute inset-0 will-change-transform" data-testid="sky-layer-haze">
        {NEBULAE.map((n) => (
          <div key={n.key} className={n.cls} style={n.style} />
        ))}
      </div>
      {/* 3 — mid-distance stars + shooting star */}
      <div ref={midRef} className="absolute inset-0 will-change-transform" data-testid="sky-layer-mid">
        {stars.filter((s) => s.tier === "mid").map(renderStar)}
        {shot && (
          <span
            key={shot.id}
            className="continua-shooting-star"
            data-testid="shooting-star"
            style={{
              left: shot.left,
              top: shot.top,
              "--ss-dx": shot.dx,
              "--ss-dy": shot.dy,
              "--ss-angle": shot.angle,
            }}
          />
        )}
      </div>
      {/* 4 — realistic thin night clouds, IN FRONT of the stars so passing
          clouds genuinely dim/obscure them (real atmospheric occlusion). */}
      <div ref={cloudFarRef} className="absolute inset-0 will-change-transform" data-testid="sky-layer-cloud-far">
        <div className="continua-cloud continua-cloud--high" />
        <div className="continua-cloud continua-cloud--far" />
      </div>
      <div ref={cloudMidRef} className="absolute inset-0 will-change-transform" data-testid="sky-layer-cloud-mid">
        <div className="continua-cloud continua-cloud--mid" />
      </div>
      {/* 5 — sparse soft foreground particles (most movement) */}
      <div ref={nearRef} className="absolute inset-0 will-change-transform" data-testid="sky-layer-near">
        {stars.filter((s) => s.tier === "near").map(renderStar)}
      </div>
    </div>
  );
};

export default StarField;
